import { format } from 'date-fns';
import { formatShopTime } from '@/utils/formatShopTime';
// DTC-F: route the parts-ETA "now" anchor in `getJobPhase` through the
// canonical clock so the parts_delay / assembly phase decision is
// deterministic under `clock.setFixed(...)`. `getBayNowAndNextTickets`
// and `formatElapsedTime` already accept `now` as a caller-supplied
// parameter and stay unchanged.
import { clock } from '@/utils/clock';

export interface BayScheduleResult {
  currentTicket: any | null;
  nextTicket: any | null;
  upcomingTickets: any[];
  completedTickets: any[];
}

/**
 * Determines current, next, upcoming, and completed tickets for a bay
 * Filters out parking lot tickets and ensures proper bay assignment logic
 */
export function getBayNowAndNextTickets(
  bayId: string,
  ticketsForDay: any[],
  now: Date
): BayScheduleResult {
  // Filter tickets for this bay, scheduled today, and NOT in parking lot.
  // Chronomancer R-LOC-10: must mirror the canonical bay-occupancy semantics
  // captured in the partial-unique index `uniq_active_bay` (migration
  // 20260421230000): physical bay occupancy requires parking_lot=false AND
  // rollover_of_ticket_id IS NULL. Without these two predicates a future-
  // day rollover Part B (which carries bay_id + parking_lot=true as a
  // future-state plan) would surface as the bay's current/upcoming ticket
  // in today's view, displacing the actual occupant.
  const bayTickets = ticketsForDay.filter(
    t => t.bay_id === bayId && !t.parking_lot && !t.rollover_of_ticket_id
  );

  // Separate into active/paused and completed
  const activeTickets = bayTickets.filter(
    t => t.status === 'Active' || t.status === 'Paused'
  );
  
  const completedTickets = bayTickets.filter(
    t => t.status === 'Completed'
  );

  // Find the "current" ticket (vehicle occupying bay right now)
  let currentTicket: any | null = null;

  // Check for running timer first (highest priority)
  const ticketWithRunningTimer = activeTickets.find(
    t => t.timer_status === 'running'
  );

  if (ticketWithRunningTimer) {
    currentTicket = ticketWithRunningTimer;
  } else {
    // Check for paused ticket that has actually started (vehicle is physically in bay)
    const pausedStartedTicket = activeTickets.find(
      t => t.status === 'Paused' && t.actual_start_time
    );
    
    if (pausedStartedTicket) {
      currentTicket = pausedStartedTicket;
    } else {
      // Check for ticket in the time window: start_time <= now < opti_finish
      const ticketsInWindow = activeTickets.filter(t => {
        if (!t.actual_start_time) return false; // Must have actually started
        
        const startTime = new Date(t.actual_start_time);
        const optiFinish = t.opti_finish ? new Date(t.opti_finish) : null;
        // CORE-AC: no invented duration. With neither a finish nor a booked duration the finish is UNKNOWN —
        // a started ticket then still occupies the bay (it has not been seen to finish), never "done after 60 min".
        const finishTime = optiFinish
          ?? (typeof t.predicted_minutes === 'number' && t.predicted_minutes > 0
            ? new Date(startTime.getTime() + t.predicted_minutes * 60000)
            : null);

        return startTime <= now && (finishTime === null || now < finishTime);
      });

      // If multiple tickets in window, prefer most recent start
      if (ticketsInWindow.length > 0) {
        currentTicket = ticketsInWindow.sort((a, b) => {
          const aStart = new Date(a.actual_start_time || a.start_time).getTime();
          const bStart = new Date(b.actual_start_time || b.start_time).getTime();
          return bStart - aStart; // Most recent first
        })[0];
      }
    }
  }

  // Fallback: Active ticket assigned to bay but timer not yet started
  // This handles tickets freshly moved into a bay via drag-and-drop
  if (!currentTicket) {
    const activeNotStarted = activeTickets.find(
      t => t.status === 'Active' && !t.actual_start_time && !t.parking_spot_id
    );
    if (activeNotStarted) {
      currentTicket = activeNotStarted;
    }
  }

  // Find remaining upcoming tickets (not current, not completed)
  const upcomingTickets = activeTickets
    .filter(t => t.id !== currentTicket?.id)
    .sort((a, b) => {
      const aTime = new Date(a.start_time || a.scheduled_start_time).getTime();
      const bTime = new Date(b.start_time || b.scheduled_start_time).getTime();
      return aTime - bTime;
    });

  // Next ticket is the first upcoming
  const nextTicket = upcomingTickets[0] || null;

  return {
    currentTicket,
    nextTicket,
    upcomingTickets,
    completedTickets: completedTickets.sort((a, b) => {
      const aTime = new Date(a.completed_at || a.updated_at).getTime();
      const bTime = new Date(b.completed_at || b.updated_at).getTime();
      return bTime - aTime; // Most recent first
    })
  };
}

/**
 * Format elapsed time from start to now
 */
export function formatElapsedTime(startTime: Date, now: Date): string {
  const diff = Math.floor((now.getTime() - startTime.getTime()) / 1000); // seconds
  const hrs = Math.floor(diff / 3600);
  const mins = Math.floor((diff % 3600) / 60);
  const secs = diff % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format time to HH:MM
 */
export function formatTimeShort(date: Date | string, shopTimezone?: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (shopTimezone) {
    return formatShopTime(d, shopTimezone, 'short');
  }
  return format(d, 'HH:mm');
}

/**
 * Get job phase from ticket data
 */
export function getJobPhase(ticket: any): {
  phase: 'teardown' | 'parts_delay' | 'assembly' | 'normal';
  partsEta?: string;
  bayLocked?: boolean;
} {
  if (!ticket?.ticket_jobs) return { phase: 'normal' };

  const jobs = ticket.ticket_jobs;
  const teardownJob = jobs.find((tj: any) => tj.teardown_enabled);
  
  if (teardownJob) {
    if (teardownJob.teardown_complete && teardownJob.parts_eta) {
      const partsEta = new Date(teardownJob.parts_eta);
      const now = clock.now();
      
      if (partsEta > now) {
        return {
          phase: 'parts_delay',
          partsEta: teardownJob.parts_eta,
          bayLocked: teardownJob.job_bay_lock,
        };
      } else {
        return {
          phase: 'assembly',
          bayLocked: teardownJob.job_bay_lock,
        };
      }
    }
    
    if (!teardownJob.teardown_complete) {
      return { phase: 'teardown' };
    }
  }

  return { phase: 'normal' };
}
